import { useEffect, useRef, useState } from 'react'

import cloneDeep from 'lodash/cloneDeep'
import useInnovaAxios from './useInnovaAxios'
import { useWellListContext } from 'components/common/hooks/WellListContext'

const useUpdateWellInfo = () => {
  const _isMounted = useRef(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasDistricts, setHasDistricts] = useState(false)
  const [hasCountrys, setHasCountrys] = useState(false)
  const [wellQc, setWellQc] = useState([])
  const isLoadingInfoRef = useRef(false)
  const fetchingQc = useRef(false)
  const isLoadingRef = useRef(false)
  const isUpdatingRef = useRef(false)
  const dropDowns = useRef({})
  const { setWellListItem } = useWellListContext()

  useEffect(() => {
    _isMounted.current = true
    getDropDowns()
    return () => {
      _isMounted.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDropDowns = useInnovaAxios({
    url: '/well/wellInfo/getWellInfoDropDowns',
  })

  const fetchWellInfo = useInnovaAxios({
    url: '/well/wellInfo/getWellInfo',
  })

  const updateWellInfo = useInnovaAxios({
    url: '/well/wellInfo/updateWellInfo',
  })

  const getQc = useInnovaAxios({
    url: '/well/QcWell',
  })

  const fetchWellQc = async (wellName) => {
    if (!wellName) return
    if (typeof wellName !== 'string') return
    if (wellName === '') return
    if (fetchingQc.current) return

    fetchingQc.current = true
    let res = await getQc({ wellName: wellName })

    fetchingQc.current = false
    if (!_isMounted.current) return
    if (res.error) return

    setWellQc(Array.isArray(res.data) ? res.data : [])
  }

  async function getWellInfo(wellName) {
    if (isLoadingInfoRef.current) {
      return { isError: true, data: 'another request is loading' }
    }
    if (typeof wellName !== 'string' || wellName === '') {
      return { isError: true, data: 'invalid well name' }
    }

    isLoadingInfoRef.current = true

    const res = await fetchWellInfo({ wellName })
    isLoadingInfoRef.current = false

    if (res.error || !res.data) {
      return { isError: true, message: res?.error?.response?.data?.error }
    }

    return { isError: false, data: res.data }
  }

  async function getDropDowns() {
    if (isLoadingRef.current) return

    isLoadingRef.current = true
    setIsLoading(true)
    const response = await fetchDropDowns()
    isLoadingRef.current = false
    setIsLoading(false)

    if (response.error) return
    if (!response.data) return
    dropDowns.current = cloneDeep(response.data)
    if (dropDowns.current.hasOwnProperty('district') && dropDowns.current.district?.length > 0) {
      setHasDistricts(true)
    }

    if (dropDowns.current.hasOwnProperty('states') && dropDowns.current.states?.length === 0) {
      setHasCountrys(true)
    }
  }

  const getDropDownList = (param, state) => {
    if (typeof param !== 'string') return []
    if (param === '') return []

    if (param === 'countys') {
      if (!dropDowns.current.hasOwnProperty('states')) return []
      if (!Array.isArray(dropDowns.current.states)) return []
      if (typeof state !== 'string') return []
      let index = dropDowns.current.states.findIndex((st) => st.stateName === state)
      if (index < 0) return []
      if (!Array.isArray(dropDowns.current.states[index].countys)) return []
      return cloneDeep(dropDowns.current.states[index].countys)
    }

    if (param === 'states') {
      if (!dropDowns.current.hasOwnProperty('states')) return []
      if (!Array.isArray(dropDowns.current.states)) return []

      if (dropDowns.current.states.length === 0)
        return Array.isArray(dropDowns.current?.countrys) ? dropDowns.current.countrys : []

      let statesDropDown = []
      for (let i = 0; i < dropDowns.current.states.length; i++) {
        statesDropDown.push(dropDowns.current.states[i].stateName)
      }

      return statesDropDown
    }

    if (param === 'wellType') {
      if (!dropDowns.current.hasOwnProperty('wellType')) return []
      if (!Array.isArray(dropDowns.current.wellType)) return []
      return cloneDeep(dropDowns.current.wellType)
    }

    if (param === 'provinces') {
      if (!dropDowns.current.hasOwnProperty('provinces')) return []
      if (!Array.isArray(dropDowns.current.provinces)) return []
      return cloneDeep(dropDowns.current.provinces)
    }

    if (param === 'district') {
      if (!dropDowns.current.hasOwnProperty('district')) return []
      if (!Array.isArray(dropDowns.current.district)) return []
      return cloneDeep(dropDowns.current.district)
    }

    if (!dropDowns.current.hasOwnProperty(param)) return []
    if (!Array.isArray(dropDowns.current[param])) return []
    return cloneDeep(dropDowns.current[param])
  }

  const handleUpdate = async (data) => {
    if (!data || !data.hasOwnProperty('actualWell')) return { isError: true, message: 'data not valid' }
    if (isUpdatingRef.current) return { isError: true, message: 'update in progress' }
    isUpdatingRef.current = true

    let getResponse = await fetchWellInfo({ wellName: data.actualWell })
    if (getResponse.error || !getResponse.data) {
      isUpdatingRef.current = false
      return { isError: true, message: getResponse?.error?.response?.data?.error }
    }

    let wellData = cloneDeep(getResponse.data)

    if (data.hasOwnProperty('wellStatus')) {
      wellData.rig = data.rig
    }

    if (data.hasOwnProperty('wellStatus')) {
      wellData.wellStatus = data.wellStatus
    }

    if (data.hasOwnProperty('state')) {
      wellData.state = data.state
    }

    if (data.hasOwnProperty('county')) {
      wellData.county = data.county
    }

    if (data.hasOwnProperty('ddCoordinator')) {
      wellData.ddCoordinator = data.ddCoordinator
    }

    if (data.hasOwnProperty('mwdCoordinator')) {
      wellData.mwdCoordinator = data.mwdCoordinator
    }

    if (data.hasOwnProperty('jobNum')) {
      wellData.jobNum = data.jobNum
    }

    if (data.hasOwnProperty('district')) {
      wellData.district = data.district
    }

    if (data.hasOwnProperty('description')) {
      wellData.description = data.description
    }

    if (data.hasOwnProperty('formation')) {
      wellData.formation = data.formation
    }

    if (data.hasOwnProperty('apiNum')) {
      wellData.apiNum = data.apiNum
    }

    //This happens if there are no daily reports for the well
    if (!wellData.wellName) {
      wellData.wellName = data.actualWell
    }

    let updateResponse = await updateWellInfo({ ...wellData })
    isUpdatingRef.current = false

    if (updateResponse.error || !updateResponse.data) {
      return { isError: true, message: updateResponse?.error?.response?.data?.error }
    }

    setWellListItem(data) // update the well list without an api fetch

    await fetchWellQc(data.actualWell)

    return { isError: false, message: '' }
  }

  return {
    wellQc,
    fetchWellQc,
    getWellInfo,
    getDropDownList,
    isLoading,
    handleUpdate,
    hasCountrys,
    hasDistricts,
  }
}

export default useUpdateWellInfo
